import { useState, useEffect } from 'react'
import { Card } from './ui/card'
import { Badge } from './ui/badge'
import { Trophy, Medal, Award, Gift } from 'lucide-react'
import { useNotification } from '../contexts/NotificationContext'
import { fetchPrizes as fetchPrizesService } from '../services/tournamentService'
import { useUser } from '../contexts/UserContext'
import { formatWinnerNames, getPrizeWinners } from '../lib/prizeWinners'
import BraggingRightsCard from './BraggingRightsCard'

/**
 * TournamentPrizes - Display prizes for a tournament
 * Shows tournament-level prizes first, then bracket-level prizes
 * Displays prizes accessible to the current user
 * 
 * @param {string} tournamentId - Tournament ID
 * @param {boolean} isAdmin - Whether user is admin
 * @param {string} groupId - Group view: only that group's prizes, and
 *   "Bragging Rights" when the owner hasn't added one
 * @param {string[]} champNames - Group view: the finished tournament's dub
 *   Champ(s), named on the Bragging Rights card
 */
export default function TournamentPrizes({ tournamentId, groupId = null, champNames = [] }) {
  const notify = useNotification()
  const { user, loading: userLoading } = useUser()
  const [tournamentPrizes, setTournamentPrizes] = useState([])
  const [bracketPrizes, setBracketPrizes] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (tournamentId && !userLoading) {
      fetchPrizes()
    }
  }, [tournamentId, groupId, userLoading, user?.id])

  const fetchPrizes = async () => {
    try {
      setLoading(true)
      const data = await fetchPrizesService({ tournamentId, groupId, asPlayer: true })
      if (!data.success) throw new Error(data.error)

      const prizes = data.prizes || []
      
      // Separate tournament-level prizes (no bracket_id) from bracket-level prizes
      const tournamentLevel = prizes.filter(p => !p.bracket_id)
      const bracketLevel = prizes.filter(p => p.bracket_id)
      
      setTournamentPrizes(tournamentLevel)
      setBracketPrizes(bracketLevel)
    } catch (err) {
      console.error('Error fetching prizes:', err)
      notify.error('Failed to load prizes')
    } finally {
      setLoading(false)
    }
  }

  const getPlaceLabel = (place) => {
    if (place === 1) return '1st Place'
    if (place === 2) return '2nd Place'
    if (place === 3) return '3rd Place'
    return `${place}th Place`
  }

  const getPlaceIcon = (place) => {
    switch (place) {
      case 1:
        return <Trophy className="h-8 w-8 text-[var(--color-crown)]" />
      case 2:
        return <Medal className="h-8 w-8 text-muted-foreground" />
      case 3:
        return <Award className="h-8 w-8 text-warning" />
      default:
        return <Gift className="h-8 w-8 text-[var(--teal-9)]" />
    }
  }

  const renderPrizeCard = (prize) => (
    <Card key={prize.id} className="p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          {getPlaceIcon(prize.place)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="font-semibold text-lg">{prize.name}</h3>
            <Badge variant={prize.group ? 'secondary' : 'outline'} className="text-xs">
              {prize.group ? prize.group.name : 'Global'}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mb-2">
            {getPlaceLabel(prize.place)}
          </p>
          {prize.description && (
            <p className="text-sm text-muted-foreground mb-2">{prize.description}</p>
          )}
          {prize.bracket && (
            <p className="text-xs text-muted-foreground">
              Bracket: {prize.bracket.name}
            </p>
          )}
          {getPrizeWinners(prize).length > 0 && (
            <div className="mt-2 pt-2 border-t">
              <p className="text-xs text-muted-foreground">
                {getPrizeWinners(prize).length > 1 ? 'Tied winners' : 'Winner'}:{' '}
                <span className="font-medium text-foreground">{formatWinnerNames(getPrizeWinners(prize))}</span>
              </p>
            </div>
          )}
        </div>
      </div>
    </Card>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading prizes...</p>
        </div>
      </div>
    )
  }

  const totalPrizes = tournamentPrizes.length + bracketPrizes.length

  // Every group tournament has a prize (owner 2026-10-07): bragging rights
  // and the dub Champ crown, unless the owner adds a real one.
  if (totalPrizes === 0 && groupId) {
    return <BraggingRightsCard champNames={champNames} className="max-w-md" />
  }

  if (totalPrizes === 0) {
    return (
      <div className="text-center py-12">
        <Trophy className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
        <p className="text-lg text-muted-foreground mb-1">No prizes yet</p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Tournament-Level Prizes */}
      {tournamentPrizes.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Tournament Prizes</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {tournamentPrizes.map(renderPrizeCard)}
          </div>
        </div>
      )}

      {/* Bracket-Level Prizes */}
      {bracketPrizes.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Bracket Prizes</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bracketPrizes.map(renderPrizeCard)}
          </div>
        </div>
      )}
    </div>
  )
}
